import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from './LoadingSpinner';
import Button from './Button';

interface ContextLoadingScreenProps {
  isLoading: boolean;
  error: string | null;
  isEmbedded: boolean;
  onRetry: () => void;
}

const ContextLoadingScreen: React.FC<ContextLoadingScreenProps> = ({
  isLoading,
  error,
  isEmbedded,
  onRetry
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-md p-8">
            <LoadingSpinner size="large" className="mx-auto mb-6" />
            <h1 className="text-xl font-semibold text-gray-900 mb-4">
              {t('embedded.loading.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {isEmbedded 
                ? t('embedded.loading.contextMessage')
                : t('embedded.loading.initializingMessage')
              }
            </p>
            <div className="bg-tenant-50 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-tenant-700">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="text-sm">
                  {t('embedded.loading.pleaseWait')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-4">
              {t('embedded.error.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {error}
            </p>
            <div className="space-y-4">
              <Button
                onClick={onRetry}
                variant="primary"
                className="w-full"
              >
                {t('embedded.error.retry')}
              </Button>
              {isEmbedded && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-orange-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-sm text-orange-800">
                      <p className="font-medium">{t('embedded.error.parentAppRequired')}</p>
                      <p>{t('embedded.error.contactSupport')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ContextLoadingScreen;